import { doc, setDoc, getDoc, collection, query, where, orderBy, limit, getDocs, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import pako from 'pako';
import * as msgpack from '@msgpack/msgpack';
import { getAuth } from 'firebase/auth';
import app from '../firebase';

export interface RecordingMetadata {
  id: string;
  authorMetadata: {
    id: number | string; // Can be number or GUID
    name: string;
    isAnonymous: boolean;
  };
  whiteboardSize: {
    width: number;
    height: number;
  };
  imageData?: {
    thumbnailUrl: string;
    snapshotUrl: string;
  };
  questionMetadata?: {
    id: string;
    title: string;
    testId: string;
    testTitle: string;
    url?: string;
    timestamp?: number;
    imageUrl?: string;
    parentImageUrl?: string;
    questionNo?: number;
    position?: {
      scale: number;
      left: number;
      top: number;
    };
  };
  solutionMetadata?: {
    solutionNumber: number;
    solutionName?: string;
    isPublic?: boolean;
    allowDownload?: boolean;
  };
  tenantMetadata: {
    id: number;
  };
  createdAt: Date | any; // Firebase Timestamp or Date
  storageInfo: {
    audioPath: string;
    actionsPath: string;
    audioSize: number;
    actionsSize: number;
  };
  duration?: number;
  // Denormalized fields for efficient querying
  tenantId?: number;
  userId?: number | string; // Can be number or GUID
  questionId?: string;
  testId?: string;
}

export interface RecordingData {
  actionData: {
    id: string;
    actions: any[];
  };
  metadata: RecordingMetadata;
}

// Helper function to remove undefined values from object
function removeUndefinedFields(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (obj instanceof Date) return obj;
  if (Array.isArray(obj)) return obj.map(removeUndefinedFields);
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      const value = obj[key];
      if (value !== undefined) {
        cleaned[key] = removeUndefinedFields(value);
      }
    }
    return cleaned;
  }
  return obj;
}

export class RecordingService {
  // Get all recordings (for debugging)
  static async getAllRecordings(): Promise<RecordingMetadata[]> {
    try {
      const querySnapshot = await getDocs(collection(db, 'recordings'));
      const recordings: RecordingMetadata[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data() as RecordingMetadata;
        recordings.push({ ...data, id: doc.id });
      });
      
      return recordings;
    } catch (error) {
      console.error('Error getting all recordings:', error);
      return [];
    }
  }

  // Test method to check Firestore connection
  static async testFirestoreConnection(): Promise<boolean> {
    try {
      const testDoc = doc(db, 'test', 'connection-test');
      await setDoc(testDoc, {
        timestamp: new Date().toISOString(),
        test: true
      });
      
      const readDoc = await getDoc(testDoc);
      if (readDoc.exists()) {
        await deleteDoc(testDoc);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Firestore connection test failed:', error);
      return false;
    }
  }
  static async getNextSolutionNumber(
    testId: string,
    questionId: string,
    tenantId: number | string,
    userId: number | string
  ): Promise<number> {
    try {
      // Query existing recordings for this question
      const q = query(
        collection(db, 'recordings'),
        where('testId', '==', testId),
        where('questionId', '==', questionId),
        where('tenantId', '==', Number(tenantId)),
        where('userId', '==', userId)
      );

      const querySnapshot = await getDocs(q);
      let maxSolutionNumber = 0;

      querySnapshot.forEach((doc) => {
        const data = doc.data() as RecordingMetadata;
        // Extract solution number from ID if exists
        const id = doc.id;
        const parts = id.split('-');
        if (parts.length >= 6) {
          const solutionNum = parseInt(parts[4]);
          if (!isNaN(solutionNum) && solutionNum > maxSolutionNumber) {
            maxSolutionNumber = solutionNum;
          }
        }
      });

      return maxSolutionNumber + 1;
    } catch (error) {
      console.error('Error getting next solution number:', error);
      return 1; // Default to 1 if error
    }
  }
  static async saveRecording(
    recordingId: string,
    actions: any[],
    audioBlob: Blob,
    metadata: Omit<RecordingMetadata, 'id' | 'createdAt' | 'storageInfo'>
  ): Promise<void> {
    
    // Validate required data
    if (!recordingId) {
      throw new Error('Recording ID is required');
    }
    if (!actions || actions.length === 0) {
      throw new Error('Recording actions are required');
    }
    if (!metadata.authorMetadata?.id) {
      throw new Error('Author ID is required in metadata');
    }
    // sessionId is passed separately, not in metadata structure
    
    // Check Firebase Auth
    const auth = getAuth(app);
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      // Try to initialize Firebase Auth
      try {
        const { initializeFirebaseAuth } = await import('./authService');
        await initializeFirebaseAuth();
        const updatedUser = auth.currentUser;
        if (!updatedUser) {
          throw new Error('Firebase authentication failed. Please login again.');
        }
      } catch (authError) {
        throw new Error('Firebase authentication required. Please login again.');
      }
    }
    
    try {
      // If questionId exists, delete existing recordings for this question
      if (metadata.questionMetadata?.id && metadata.authorMetadata.id && metadata.tenantMetadata.id) {
     
        await this.deleteRecordingsByQuestion(
          metadata.tenantMetadata.id,
          metadata.authorMetadata.id,
          metadata.questionMetadata.id
        );
      }
      
      // Compress actions data
      const actionsData = {
        id: recordingId,
        actions: actions,
      };
      const serialized = msgpack.encode(actionsData);
      const compressed = pako.gzip(serialized);
      const actionsBlob = new Blob([compressed], { type: 'application/octet-stream' });

      // Upload to Storage with tenant-based path organization
      const tenantId = metadata.tenantMetadata.id;
      const audioRef = ref(storage, `recordings/${tenantId}/${recordingId}/audio`);
      const actionsRef = ref(storage, `recordings/${tenantId}/${recordingId}/actions`);
      
      try {
        await Promise.all([
          uploadBytes(audioRef, audioBlob),
          uploadBytes(actionsRef, actionsBlob),
        ]);
      } catch (storageError) {
        throw new Error(`Storage upload failed: ${(storageError as any)?.message || 'Unknown error'}`);
      }

      // Save metadata to Firestore with denormalized fields for querying
      const timestamp = new Date().toISOString();
      const fullMetadata = {
        ...metadata,
        id: recordingId,
        createdAt: timestamp, // Convert to ISO string for Firestore
        storageInfo: {
          audioPath: `recordings/${tenantId}/${recordingId}/audio`,
          actionsPath: `recordings/${tenantId}/${recordingId}/actions`,
          audioSize: audioBlob.size,
          actionsSize: actionsBlob.size,
          lastModified: timestamp
        },
        // Denormalized fields for efficient querying
        tenantId: Number(metadata.tenantMetadata.id),
        userId: metadata.authorMetadata.id, // Can be number or string (GUID)
        questionId: metadata.questionMetadata?.id || null,
        testId: metadata.questionMetadata?.testId || null,
        // Add validation metadata
        _validation: {
          hasAudio: audioBlob.size > 0,
          hasActions: actions.length > 0,
          hasAuthorId: !!metadata.authorMetadata?.id,
          hasTenantId: !!metadata.tenantMetadata?.id,
          savedAt: timestamp
        }
      };
      
      try {
        const docRef = doc(db, 'recordings', recordingId);
        
        // Remove undefined fields before saving to Firestore
        const cleanedMetadata = removeUndefinedFields(fullMetadata);
        
        await setDoc(docRef, cleanedMetadata);
        
        // Verify the document was created
        const verifyDoc = await getDoc(docRef);
        if (!verifyDoc.exists()) {
          throw new Error('Document verification failed - recording not saved to Firestore');
        }
      } catch (firestoreError) {
        throw firestoreError;
      }
      
    } catch (error) {
      throw error;
    }
  }

  static async getRecording(recordingId: string): Promise<RecordingData | null> {
    try {
      // Get metadata from Firestore
      const docRef = doc(db, 'recordings', recordingId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const metadata = docSnap.data() as RecordingMetadata;

      // Get actions data from Storage
      const actionsRef = ref(storage, metadata.storageInfo.actionsPath);
      const actionsUrl = await getDownloadURL(actionsRef);
      
      const response = await fetch(actionsUrl);
      const compressed = await response.arrayBuffer();
      const decompressed = pako.ungzip(new Uint8Array(compressed));
      const actionData = msgpack.decode(decompressed) as { id: string; actions: any[] };


      return {
        actionData,
        metadata,
      };
    } catch (error) {
      throw error;
    }
  }

  static async getRecordingAudioUrl(audioPath: string): Promise<string> {
    try {
      const audioRef = ref(storage, audioPath);
      const audioUrl = await getDownloadURL(audioRef);
      return audioUrl;
    } catch (error) {
      console.error('Error getting audio URL:', error);
      throw error;
    }
  }

  static async getRecordingsByQuestion(
    questionId: string,
    userId: string,
    limitCount: number = 10
  ): Promise<RecordingMetadata[]> {
    try {
      // Use denormalized fields for more efficient querying
      const q = query(
        collection(db, 'recordings'),
        where('questionId', '==', questionId),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const recordings: RecordingMetadata[] = [];
      
      querySnapshot.forEach((doc) => {
        recordings.push({ ...doc.data(), id: doc.id } as RecordingMetadata);
      });

      return recordings;
    } catch (error) {
      // If index error, try without ordering
      if (error instanceof Error && error.message.includes('index')) {
          
        const simpleQuery = query(
          collection(db, 'recordings'),
          where('questionId', '==', questionId),
          where('userId', '==', userId)
        );
        
        const querySnapshot = await getDocs(simpleQuery);
        const recordings: RecordingMetadata[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data() as RecordingMetadata;
          recordings.push({ ...data, id: doc.id });
        });
        
        // Sort manually by createdAt descending
        recordings.sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime();
        });
        
        // Limit results manually
        return recordings.slice(0, limitCount);
      }
      throw error;
    }
  }

  static async getPublicRecordingsByQuestion(
    questionId: string,
    limitCount: number = 10
  ): Promise<RecordingMetadata[]> {
    try {
      const q = query(
        collection(db, 'recordings'),
        where('questionId', '==', questionId),
        where('solutionMetadata.isPublic', '==', true),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const recordings: RecordingMetadata[] = [];
      
      querySnapshot.forEach((doc) => {
        recordings.push({ ...doc.data(), id: doc.id } as RecordingMetadata);
      });

      return recordings;
    } catch (error) {
      // If index error, try without ordering
      if (error instanceof Error && error.message.includes('index')) {
          
        const simpleQuery = query(
          collection(db, 'recordings'),
          where('questionId', '==', questionId),
          where('solutionMetadata.isPublic', '==', true)
        );
        
        const querySnapshot = await getDocs(simpleQuery);
        const recordings: RecordingMetadata[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data() as RecordingMetadata;
          recordings.push({ ...data, id: doc.id });
        });
        
        // Sort manually by createdAt descending
        recordings.sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime();
        });
        
        // Limit results manually
        return recordings.slice(0, limitCount);
      }
      throw error;
    }
  }

  static async getTenantRecordingsByQuestion(
    questionId: string,
    tenantId: string,
    limitCount: number = 10
  ): Promise<RecordingMetadata[]> {
    try {
      const q = query(
        collection(db, 'recordings'),
        where('questionId', '==', questionId),
        where('tenantId', '==', Number(tenantId)),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const recordings: RecordingMetadata[] = [];
      
      querySnapshot.forEach((doc) => {
        recordings.push({ ...doc.data(), id: doc.id } as RecordingMetadata);
      });

      return recordings;
    } catch (error) {
      // If index error, try without ordering
      if (error instanceof Error && error.message.includes('index')) {
          
        const simpleQuery = query(
          collection(db, 'recordings'),
          where('questionId', '==', questionId),
          where('tenantId', '==', Number(tenantId))
        );
        
        const querySnapshot = await getDocs(simpleQuery);
        const recordings: RecordingMetadata[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data() as RecordingMetadata;
          recordings.push({ ...data, id: doc.id });
        });
        
        // Sort manually by createdAt descending
        recordings.sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime();
        });
        
        // Limit results manually
        return recordings.slice(0, limitCount);
      }
      throw error;
    }
  }

  static async getSolutionsByQuestion(
    testId: string,
    questionId: string,
    tenantId: number | string,
    userId?: number | string
  ): Promise<RecordingMetadata[]> {
    try {
      
      let q;
      if (userId) {
        // Get solutions for specific user
        q = query(
          collection(db, 'recordings'),
          where('testId', '==', testId),
          where('questionId', '==', questionId),
          where('tenantId', '==', Number(tenantId)),
          where('userId', '==', userId),
          orderBy('solutionMetadata.solutionNumber', 'asc'),
          limit(100) // Maksimum 100 çözüm getir
        );
      } else {
        // Get all solutions for question (for teachers/admins)
        q = query(
          collection(db, 'recordings'),
          where('testId', '==', testId),
          where('questionId', '==', questionId),
          where('tenantId', '==', Number(tenantId)),
          orderBy('solutionMetadata.solutionNumber', 'asc'),
          limit(100) // Maksimum 100 çözüm getir
        );
      }

      const querySnapshot = await getDocs(q);
      const solutions: RecordingMetadata[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data() as RecordingMetadata;
        solutions.push({ ...data, id: doc.id });
      });

      return solutions;
    } catch (error) {
      console.error('Error getting solutions:', error);
      // If error is about missing index, try without ordering
      if (error instanceof Error && error.message.includes('index')) {
        
        let q;
        if (userId) {
          q = query(
            collection(db, 'recordings'),
            where('questionMetadata.testId', '==', testId),
            where('questionMetadata.id', '==', questionId),
            where('tenantMetadata.id', '==', Number(tenantId)),
            where('authorMetadata.id', '==', Number(userId))
          );
        } else {
          q = query(
            collection(db, 'recordings'),
            where('questionMetadata.testId', '==', testId),
            where('questionMetadata.id', '==', questionId),
            where('tenantMetadata.id', '==', Number(tenantId))
          );
        }

        const querySnapshot = await getDocs(q);
        const solutions: RecordingMetadata[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data() as RecordingMetadata;
          solutions.push({ ...data, id: doc.id });
        });

        // Sort manually by solution number
        solutions.sort((a, b) => {
          const aNum = a.solutionMetadata?.solutionNumber || 0;
          const bNum = b.solutionMetadata?.solutionNumber || 0;
          return aNum - bNum;
        });

        return solutions;
      }
      throw error;
    }
  }

  static async getRecordingsByTest(
    testId: string,
    tenantId: number | string,
    userId?: number | string,
    limitCount: number = 50
  ): Promise<RecordingMetadata[]> {
    try {
      
      let recordings: RecordingMetadata[] = [];
      
      try {
        // First try with composite query
        let q;
        if (userId) {
          q = query(
            collection(db, 'recordings'),
            where('testId', '==', testId),
            where('tenantId', '==', Number(tenantId)),
            where('userId', '==', userId),
            orderBy('createdAt', 'desc'),
            limit(limitCount)
          );
        } else {
          q = query(
            collection(db, 'recordings'),
            where('testId', '==', testId),
            where('tenantId', '==', Number(tenantId)),
            orderBy('createdAt', 'desc'),
            limit(limitCount)
          );
        }

        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
          const data = doc.data() as RecordingMetadata;
          recordings.push({ ...data, id: doc.id });
        });
        
      } catch (indexError) {
        
        // Fallback: Get all recordings and filter client-side
        const allRecordings = await getDocs(collection(db, 'recordings'));
        
        allRecordings.forEach((doc) => {
          const data = doc.data() as RecordingMetadata;
          
          // Manual filtering
          if (data.testId === testId && 
              data.tenantId === Number(tenantId) &&
              (!userId || data.userId === userId)) {
            recordings.push({ ...data, id: doc.id });
          }
        });
        
        // Sort by createdAt descending
        recordings.sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime();
        });
        
        // Limit results
        recordings = recordings.slice(0, limitCount);
      }

      return recordings;
    } catch (error) {
      console.error('Error getting recordings by test:', error);
      return [];
    }
  }

  static async getRecordingAudioUrlById(recordingId: string): Promise<string | null> {
    try {
      const docRef = doc(db, 'recordings', recordingId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const metadata = docSnap.data() as RecordingMetadata;
      const audioRef = ref(storage, metadata.storageInfo.audioPath);
      return await getDownloadURL(audioRef);
    } catch (error) {
      throw error;
    }
  }

  static async deleteRecordingsByQuestion(
    tenantId: number,
    userId: number| string,
    questionId: string
  ): Promise<void> {
    try {
      // Query recordings for this tenant, user, and question using denormalized fields
      const q = query(
        collection(db, 'recordings'),
        where('tenantId', '==', tenantId),
        where('userId', '==', userId),
        where('questionId', '==', questionId)
      );

      const querySnapshot = await getDocs(q);
      
      // Delete each recording
      const deletePromises = querySnapshot.docs.map(async (docSnapshot) => {
        const metadata = docSnapshot.data() as RecordingMetadata;
        
        // Delete from storage
        const audioRef = ref(storage, metadata.storageInfo.audioPath);
        const actionsRef = ref(storage, metadata.storageInfo.actionsPath);
        
        await Promise.all([
          deleteObject(audioRef).catch(() => {}), // Ignore if not exists
          deleteObject(actionsRef).catch(() => {})
        ]);
        
        // Delete from Firestore
        await deleteDoc(doc(db, 'recordings', docSnapshot.id));
      });

      await Promise.all(deletePromises);
    } catch (error) {
      console.error('Error deleting recordings:', error);
      // Don't throw - continue with saving new recording
    }
  }

  static async deleteRecording(recordingId: string): Promise<void> {
    try {
      
      // Get recording metadata first
      const docRef = doc(db, 'recordings', recordingId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Recording not found');
      }
      
      const metadata = docSnap.data() as RecordingMetadata;
      
      // Delete from storage
      const audioRef = ref(storage, metadata.storageInfo.audioPath);
      const actionsRef = ref(storage, metadata.storageInfo.actionsPath);
      
      await Promise.all([
        deleteObject(audioRef).catch(() => {}),
        deleteObject(actionsRef).catch(() => {})
      ]);
      
      // Delete from Firestore
      await deleteDoc(docRef);
      
    } catch (error) {
      console.error('Error deleting recording:', error);
      throw error;
    }
  }
}